import { createContext, useCallback, useContext, useEffect, useRef } from 'react'
import { useWorkspace } from './WorkspaceContext'
import type { BackendMessage } from '../api/backend'
import { getBackendUrl } from '../lib/backendUrl'

// ─── Message Types ────────────────────────────────────────────────────────────

export interface WsClientMessage {
  type: 'subscribe' | 'unsubscribe' | 'user_message' | 'ping'
  chatId?: string
  content?: string
  attachments?: Array<{
    storageId: string
    filename: string
    mimeType: string
    storageName?: string
  }>
  skillId?: string
}

export interface WsServerMessage {
  type:
    | 'history'
    | 'user_message'
    | 'assistant_message'
    | 'assistant_done'
    | 'assistant_delta'
    | 'assistant_message_with_image'
    | 'tool_use'
    | 'image_generation_progress'
    | 'queue_status'
    | 'result'
    | 'pong'
    | 'error'
  payload?: {
    chatId?: string
    messages?: BackendMessage[]
    content?: string
    delta?: string
    success?: boolean
    message?: string
    /** Image blob URL for preview (assistant_message_with_image) */
    blobUrl?: string
    /** Image ID for download (assistant_message_with_image) */
    id?: string
  }
  /** Server may put fields at root level instead of under payload */
  [key: string]: unknown
}

// ─── Context ──────────────────────────────────────────────────────────────────

interface WebSocketContextValue {
  subscribe: (chatId: string) => void
  unsubscribe: (chatId: string) => void
  sendUserMessage: (
    chatId: string,
    content: string,
    attachments?: Array<{
      storageId: string
      filename: string
      mimeType: string
      storageName?: string
    }>,
  ) => void
  addMessageHandler: (handler: (msg: WsServerMessage) => void) => () => void
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null)

// ─── URL Helpers ──────────────────────────────────────────────────────────────

const SKILL_ID = (import.meta.env.VITE_SKILL_ID as string | undefined) ?? 'robolabx'

export function getWsUrl(workspaceId: string): string {
  const wsBase = getBackendUrl().replace(/^https/, 'wss').replace(/^http/, 'ws')
  return `${wsBase}/robo/ws?workspaceId=${encodeURIComponent(workspaceId)}`
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function WebSocketProvider({ children }: { children: React.ReactNode }) {
  const { isResolved, workspaceId } = useWorkspace()

  const wsRef = useRef<WebSocket | null>(null)
  const handlersRef = useRef<Set<(msg: WsServerMessage) => void>>(new Set())
  const activeSubscriptionsRef = useRef<Set<string>>(new Set())
  const pendingMessagesRef = useRef<WsClientMessage[]>([])
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const isUnmountedRef = useRef(false)

  // Use a ref for connect so the onclose callback always calls the latest version
  const connectRef = useRef<(wsId: string) => void>(() => {})

  const connect = useCallback((wsId: string) => {
    if (isUnmountedRef.current) return

    const url = getWsUrl(wsId)
    const ws = new WebSocket(url)
    wsRef.current = ws

    ws.onopen = () => {
      reconnectAttemptsRef.current = 0

      // Re-subscribe to all active sessions (handles reconnect)
      activeSubscriptionsRef.current.forEach((chatId) => {
        ws.send(JSON.stringify({ type: 'subscribe', chatId, skillId: SKILL_ID }))
      })

      // Flush any messages queued while the socket was connecting
      const pending = pendingMessagesRef.current.splice(0)
      for (const msg of pending) {
        ws.send(JSON.stringify(msg))
      }

      // Start heartbeat
      pingIntervalRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }))
        }
      }, 30_000)
    }

    ws.onmessage = (event: MessageEvent) => {
      try {
        const msg = JSON.parse(event.data as string) as WsServerMessage
        if (msg.type === 'pong') return
        handlersRef.current.forEach((handler) => handler(msg))
      } catch {
        // ignore malformed messages
      }
    }

    ws.onclose = () => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current)
        pingIntervalRef.current = null
      }
      if (isUnmountedRef.current) return
      // Exponential backoff: 1s, 2s, 4s, ..., max 30s
      const delay = Math.min(1_000 * 2 ** reconnectAttemptsRef.current, 30_000)
      reconnectAttemptsRef.current++
      reconnectTimeoutRef.current = setTimeout(() => connectRef.current(wsId), delay)
    }

    ws.onerror = () => {
      ws.close()
    }
  }, [])

  // Keep connectRef in sync with the latest connect function
  useEffect(() => {
    connectRef.current = connect
  }, [connect])

  // Connect only after workspace is fully resolved (workspaceId is available)
  useEffect(() => {
    if (!isResolved || !workspaceId) return

    isUnmountedRef.current = false
    connect(workspaceId)

    return () => {
      isUnmountedRef.current = true
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current)
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current)
      wsRef.current?.close()
    }
  // workspaceId is stable once set; re-connecting on id change is intentional
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResolved, workspaceId])

  const send = useCallback((msg: WsClientMessage) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg))
    } else {
      // Queue messages while the socket is connecting — they'll be flushed on open
      pendingMessagesRef.current.push(msg)
    }
  }, [])

  const sendInOrder = useCallback((messages: WsClientMessage[]) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      for (const message of messages) {
        wsRef.current.send(JSON.stringify(message))
      }
      return
    }
    pendingMessagesRef.current.push(...messages)
  }, [])

  const subscribe = useCallback(
    (chatId: string) => {
      if (activeSubscriptionsRef.current.has(chatId)) return
      activeSubscriptionsRef.current.add(chatId)
      send({ type: 'subscribe', chatId, skillId: SKILL_ID })
    },
    [send],
  )

  const unsubscribe = useCallback(
    (chatId: string) => {
      activeSubscriptionsRef.current.delete(chatId)
      send({ type: 'unsubscribe', chatId, skillId: SKILL_ID })
    },
    [send],
  )

  const sendUserMessage = useCallback(
    (
      chatId: string,
      content: string,
      attachments?: Array<{
        storageId: string
        filename: string
        mimeType: string
        storageName?: string
      }>,
    ) => {
      // Ensure the chat is subscribed before sending the first message.
      // This avoids a race when a newly created chat sends immediately
      // before route-sync has had a chance to call `subscribe(chatId)`.
      const isAlreadySubscribed = activeSubscriptionsRef.current.has(chatId)
      activeSubscriptionsRef.current.add(chatId)

      if (!isAlreadySubscribed) {
        sendInOrder([
          { type: 'subscribe', chatId, skillId: SKILL_ID },
          {
            type: 'user_message',
            chatId,
            content,
            attachments: attachments && attachments.length > 0 ? attachments : undefined,
            skillId: SKILL_ID,
          },
        ])
        return
      }

      send({
        type: 'user_message',
        chatId,
        content,
        attachments: attachments && attachments.length > 0 ? attachments : undefined,
        skillId: SKILL_ID,
      })
    },
    [send, sendInOrder],
  )

  const addMessageHandler = useCallback((handler: (msg: WsServerMessage) => void) => {
    handlersRef.current.add(handler)
    return () => {
      handlersRef.current.delete(handler)
    }
  }, [])

  return (
    <WebSocketContext.Provider value={{ subscribe, unsubscribe, sendUserMessage, addMessageHandler }}>
      {children}
    </WebSocketContext.Provider>
  )
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useWebSocket(): WebSocketContextValue {
  const ctx = useContext(WebSocketContext)
  if (!ctx) throw new Error('useWebSocket must be used within WebSocketProvider')
  return ctx
}
